'use client';
import './productCard.scss';

import SatelliteAltOutlinedIcon from '@mui/icons-material/SatelliteAltOutlined';
import SatelliteOutlinedIcon from '@mui/icons-material/SatelliteOutlined';
import { Box, Card, CardContent, CardMedia, Typography, Stack, Divider } from '@mui/material';
/* eslint-disable @next/next/no-img-element */
/* import libraries */
import moment from 'moment';
import { useState } from 'react';

import orderType from '@/constants/enums/orderType';
import {
  aeralResoluitionText,
  checkForResTextForAerial,
  checkVantor,
  checkVantorBands,
  existingImgResText,
  imgTypeTextConvertor,
  newImageCartResolutionText,
  priceNumberFormat,
} from '@/lib/utils';
import { CartItemCardData } from './cartInterfaces';
import DeleteDialog from './dialog/deleteDialog';
import AddOrderName from './addOrderName';
import { useTranslation } from 'react-i18next';

interface CartItemProps {
  type: orderType;
  cardData: CartItemCardData;
  overlap?: string | null;
  overlapData?: CartItemCardData[] | [];
  id: string;
}

export default function CartProduct({ type, cardData, overlap, overlapData, id }: CartItemProps) {
  const [imageUrl, setImageUrl] = useState<string>(cardData?.thumbnailImg ?? '');
  const [isTextField, setIsTextField] = useState<boolean>(false);
  const isVantor = checkVantor(cardData);
  const vantorBands = checkVantorBands(cardData);
  const { t } = useTranslation('cart');

  const formatDateRange = () => {
    if (type === orderType.NEW) {
      const startDate = moment(cardData?.startDate).format('MMM DD, YYYY');
      const endDate = moment(cardData?.lastDate).format('MMM DD, YYYY');
      return `${startDate} - ${endDate}`;
    }
    return cardData?.date;
  };

  const getResolutionText = (offeringId: any) => {
    return type === orderType.NEW
      ? newImageCartResolutionText(cardData?.resolution)
      : checkForResTextForAerial(offeringId)
        ? aeralResoluitionText(offeringId, 'cart-drawer')
        : existingImgResText(
            typeof cardData?.resolution === 'number' ? cardData?.resolution : 0,
            true
          );
  };

  const getImageTypeText = () => {
    if (cardData?.isPanchromatic) return null;
    return type === orderType.NEW
      ? imgTypeTextConvertor(cardData?.imageTypes)
      : imgTypeTextConvertor(cardData?.outputId);
  };

  const itemId = cardData?.id ?? cardData?.uu_id ?? '';
  const price = cardData?.price ? priceNumberFormat(cardData?.price) : 0;
  const isGuaranteed =
    type === orderType.NEW &&
    cardData?.taskingPriority === 'guaranteed' &&
    cardData?.dataType === 'optical';
  const isEnhancedTasking =
    type === orderType.NEW &&
    cardData?.taskingPriority === 'enhanced' &&
    cardData?.dataType === 'optical';
  const isOneDayPriority = isGuaranteed && cardData?.priorityDay === 'one_day';
  const isOneNowWeather = isGuaranteed && cardData?.priorityDay === 'one_now_weather';
  const isVantorFastView = isGuaranteed && cardData?.source?.includes('Vantor');
  const isSkySatAssured = isGuaranteed && cardData?.source === 'SkySat';

  const isDroneTasking = type === orderType.NEW && cardData?.dataType === 'drone';
  return (
    <Card className="product-card-cart product-card">
      <AddOrderName
        isTextField={isTextField}
        setIsTextField={setIsTextField}
        cardData={cardData}
        overlap={overlap ?? undefined}
        id={id}
        className="cart-drawer-add-order-name"
      />
      <Box className="card-content-wrapper">
        <Box className="img-wrapper">
          {type === orderType.NEW &&
          !Object.prototype.hasOwnProperty.call(cardData, 'thumbnailImg') ? (
            <Box className="img-thumbnail" aria-label="order-new-image-thumbnail"></Box>
          ) : imageUrl === '' ? (
            <Box className="img-wrapper">
              <Box className="img-thumbnail" aria-label="order-new-image-thumbnail"></Box>
              <SatelliteAltOutlinedIcon aria-label="order-new-image-icon" />
            </Box>
          ) : (
            <>
              <CardMedia
                component="img"
                image={imageUrl}
                aria-label="existing-order-image"
                alt="Cart Item Picture"
                onError={(e: any) => {
                  e.target.onerror = null;
                  setImageUrl('');
                }}
              />
              {/* multi image icon here */}
              {overlap && <i className="icon-combine"></i>}
              {!overlap && <i className="icon-archive"></i>}
            </>
          )}
          {type === orderType.NEW && <SatelliteAltOutlinedIcon aria-label="order-new-image-icon" />}
          {cardData?.isPanchromatic && (
            <SatelliteOutlinedIcon
              aria-label="panchromatic-image-thumbnail"
              sx={{
                color: 'black',
                backgroundColor: 'white',
                fontSize: '14px',
                borderRadius: '2px',
              }}
            />
          )}
        </Box>
        <CardContent>
          <Box className="card-info-wrapper">
            <Box className="card-info">
              {!isGuaranteed && (
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="caption" display="block">
                    {formatDateRange()}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {cardData?.locationName}
                  </Typography>

                  <Typography variant="caption" display="block">
                    {isDroneTasking ? 'Spexi Orthomosaic 2.8cm' : cardData?.source}
                  </Typography>

                  {isEnhancedTasking && (
                    <Typography variant="caption" display="block">
                      {t('orderImagery:enhancedPriority', 'Enhanced Priority')}
                    </Typography>
                  )}
                  {!isDroneTasking && (
                    <Typography variant="caption" display="block">
                      {getResolutionText(cardData?.archiveOfferingId)}
                    </Typography>
                  )}

                  {type === orderType.EXISTING && isVantor && (
                    <Typography variant="caption" display="block">
                      {vantorBands}
                    </Typography>
                  )}
                  {type === orderType.EXISTING && cardData?.isSensorElevation && (
                    <Typography variant="caption" display="block">
                      {cardData?.bundleName}
                    </Typography>
                  )}
                </Stack>
              )}
              {isGuaranteed && (
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="caption" display="block">
                    {isOneDayPriority || isVantorFastView || isSkySatAssured
                      ? `${moment(cardData?.suggestedDate).format('MMM DD, YYYY')} - ${moment(cardData?.suggestedDate).format('MMM DD, YYYY')}`
                      : `${moment(cardData?.startDate).format('MMM DD, YYYY')} - ${moment(cardData?.lastDate).format('MMM DD, YYYY')}`}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {cardData?.locationName}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {`${cardData?.source} ${isOneDayPriority ? 'OneDay' : isOneNowWeather ? 'OneNow Weather' : ''}`}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {t('guaranteedPriority', 'Guaranteed Priority')}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {isVantorFastView
                      ? getResolutionText(cardData?.archiveOfferingId)
                      : cardData?.newResolution?.resolution_high
                        ? `${Math.round((cardData.newResolution.resolution_high ?? 0) * 100)}cm`
                        : '30cm'}
                  </Typography>
                </Stack>
              )}
            </Box>
            <Box className="card-info">
              <DeleteDialog itemId={overlap ? id : itemId} />
              <Typography variant="h6">{`$ ${price}`}</Typography>
            </Box>
          </Box>
        </CardContent>
      </Box>
      {overlap && (
        <Box sx={{ marginTop: '5px' }}>
          <Typography variant="caption">{`${overlapData?.length} ${overlapData && overlapData?.length > 1 ? t('images') : t('image')}`}</Typography>
        </Box>
      )}
      <Divider sx={{ marginTop: '8px', color: '#F4F6F5' }} />
    </Card>
  );
}
